import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import MarketplacePreview from "@/components/MarketplacePreview";
import { Button } from "@/components/ui/button";
import { TextEditor, Whiteboard } from "@/components/classroom";
import ExerciseRenderer from "@/components/puzzle-player/ExerciseRenderer";
import { homepageDemoExercises, fixedShuffle } from "@/data/mock-puzzles";
import { usePlatformStats } from "@/hooks/usePlatformStats";
import { exerciseTypeToEnum } from "@/lib/puzzles";
import type { Exercise, ExerciseType, PlayProps } from "@/types/puzzle";
import {
  Calendar,
  Check,
  CircleHelp,
  Combine,
  FileText,
  Globe2,
  Languages,
  MessageSquare,
  Mic,
  PenTool,
  Pencil,
  RotateCcw,
  Settings,
  Shuffle,
  TextCursor,
  Users,
  Volume2,
} from "lucide-react";

const VideoTile = ({
  initial,
  name,
  role,
  bgClass,
  ringClass,
}: {
  initial: string;
  name: string;
  role: string;
  bgClass: string;
  ringClass: string;
}) => (
  <div
    className={`relative flex-1 rounded-lg overflow-hidden border border-border ${bgClass}`}
  >
    <div className="absolute inset-0 flex items-center justify-center">
      <div
        className={`w-16 h-16 rounded-full bg-card flex items-center justify-center font-display text-2xl font-bold text-foreground border-2 ${ringClass}`}
      >
        {initial}
      </div>
    </div>
    <div className="absolute bottom-2 left-2 px-2 py-0.5 rounded-md bg-foreground/70 text-background text-[10px] font-medium">
      {name}
    </div>
    <div className="absolute bottom-2 right-2 w-5 h-5 rounded-full bg-card border border-border flex items-center justify-center">
      <Mic className="w-2.5 h-2.5 text-foreground" />
    </div>
    <div className="absolute top-2 left-2 text-[10px] uppercase tracking-widest text-foreground/60 font-semibold">
      {role}
    </div>
  </div>
);

const ClassroomVideoUI = () => {
  const { t } = useTranslation();
  return (
  <div className="rounded-3xl bg-background border border-border overflow-hidden shadow-soft">
    <div className="flex items-center justify-between px-4 h-12 border-b border-border text-xs bg-card">
      <div className="flex items-center gap-3">
        <div className="w-7 h-7 rounded-md bg-primary text-primary-foreground flex items-center justify-center font-display font-bold text-sm">
          J
        </div>
        <span className="text-muted-foreground hidden sm:inline">
          {t("home.howItWorks.classroom.lessonTitle")}
        </span>
        <span className="hidden md:inline-flex items-center gap-1 px-2 py-1 rounded-md border border-border text-muted-foreground">
          <Calendar className="w-3 h-3" />
          {t("home.howItWorks.classroom.schedule")}
        </span>
      </div>
      <div className="flex items-center gap-1">
        <span className="px-2.5 py-1 rounded-md bg-foreground text-background font-medium flex items-center gap-1.5">
          <PenTool className="w-3 h-3" />
          <span className="hidden sm:inline">{t("home.howItWorks.classroom.whiteboard")}</span>
        </span>
        <span className="px-2.5 py-1 rounded-md bg-foreground text-background font-medium flex items-center gap-1.5">
          <FileText className="w-3 h-3" />
          <span className="hidden sm:inline">{t("home.howItWorks.classroom.notes")}</span>
        </span>
        <span className="px-2.5 py-1 rounded-md text-muted-foreground flex items-center gap-1.5">
          <MessageSquare className="w-3 h-3" />
          <span className="hidden sm:inline">{t("home.howItWorks.classroom.chat")}</span>
        </span>
      </div>
    </div>

    <div className="grid grid-cols-1 sm:grid-cols-12 sm:aspect-[16/9]">
      <div className="sm:col-span-3 border-b sm:border-b-0 sm:border-r border-border bg-card flex flex-col">
        <div className="flex items-center px-3 h-8 border-b border-border text-[10px] uppercase tracking-widest text-muted-foreground">
          {t("home.howItWorks.classroom.video")}
        </div>
        <div className="flex-none h-32 sm:h-auto sm:flex-1 p-3 flex flex-row sm:flex-col gap-2">
          <VideoTile
            initial="S"
            name="Sofía"
            role={t("home.howItWorks.classroom.tutorRole")}
            bgClass="bg-primary/20"
            ringClass="border-primary"
          />
          <VideoTile
            initial="M"
            name="Mateo"
            role={t("home.howItWorks.classroom.studentRole")}
            bgClass="bg-secondary"
            ringClass="border-border"
          />
        </div>
      </div>
      <div className="sm:col-span-9 grid sm:grid-rows-2">
        <div className="bg-background flex flex-col border-b border-border overflow-hidden min-h-[200px] sm:min-h-0">
          <Whiteboard />
        </div>
        <div className="bg-background flex flex-col overflow-hidden min-h-[160px] sm:min-h-0 [&_.overflow-y-auto]:overflow-hidden">
          <TextEditor />
        </div>
      </div>
    </div>
  </div>
  );
};

const FlashSwipeCard = () => {
  const { t } = useTranslation();
  const [flipped, setFlipped] = useState(false);

  const stats = [
    { n: "12", l: t("home.howItWorks.flashcards.stats.dueToday") },
    { n: "248", l: t("home.howItWorks.flashcards.stats.inDeck") },
    { n: "94%", l: t("home.howItWorks.flashcards.stats.retention") },
  ];

  return (
    <div className="grid md:grid-cols-2 gap-8 items-center">
      <div
        className="relative h-[400px] flex items-center justify-center select-none"
        style={{ perspective: "1200px" }}
      >
        <div className="absolute w-72 h-80 rounded-3xl bg-primary/20 rotate-[-6deg] translate-x-4 translate-y-2 shadow-soft" />
        <div className="absolute w-72 h-80 rounded-3xl bg-primary rotate-[3deg] -translate-x-4 shadow-soft" />
        <div
          role="button"
          tabIndex={0}
          onClick={() => setFlipped((f) => !f)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setFlipped((f) => !f);
            }
          }}
          aria-label={t("home.howItWorks.flashcards.flipAriaLabel")}
          className="relative w-72 h-80 cursor-pointer rounded-3xl focus-ring"
        >
          <div
            className="w-full h-full relative"
            style={{
              transformStyle: "preserve-3d",
              transition: "transform 0.5s",
              transform: flipped ? "rotateY(180deg)" : "rotateY(0deg)",
            }}
          >
            <div
              className="absolute inset-0 rounded-3xl bg-card border border-border shadow-soft p-6 flex flex-col"
              style={{ backfaceVisibility: "hidden" }}
            >
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span className="px-2 py-1 rounded-full bg-secondary">
                  {t("home.howItWorks.flashcards.partOfSpeech")}
                </span>
                <button
                  onPointerDown={(e) => e.stopPropagation()}
                  onPointerUp={(e) => e.stopPropagation()}
                  onPointerMove={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (typeof window === "undefined" || !window.speechSynthesis) return;
                    const utterance = new SpeechSynthesisUtterance("図書館");
                    utterance.lang = "ja-JP";
                    utterance.rate = 0.9;
                    window.speechSynthesis.cancel();
                    window.speechSynthesis.speak(utterance);
                  }}
                  aria-label={t("home.howItWorks.flashcards.playPronunciationAriaLabel")}
                  className="w-8 h-8 rounded-full bg-secondary hover:bg-primary hover:text-primary-foreground transition flex items-center justify-center focus-ring"
                >
                  <Volume2 className="w-4 h-4" />
                </button>
              </div>
              <div className="flex-1 flex flex-col items-center justify-center text-center">
                <div className="font-display text-6xl">{t("home.howItWorks.flashcards.word")}</div>
                <div className="text-sm text-muted-foreground mt-2">
                  {t("home.howItWorks.flashcards.reading")}
                </div>
              </div>
              <div className="text-center text-xs text-muted-foreground italic">
                {t("home.howItWorks.flashcards.tapToReveal")}
              </div>
            </div>
            <div
              className="absolute inset-0 rounded-3xl bg-foreground text-background p-6 flex flex-col items-center justify-center text-center"
              style={{
                backfaceVisibility: "hidden",
                transform: "rotateY(180deg)",
              }}
            >
              <div className="text-xs uppercase tracking-widest text-primary mb-3">
                {t("home.howItWorks.flashcards.meaningLabel")}
              </div>
              <div className="font-display text-5xl">{t("home.howItWorks.flashcards.meaning")}</div>
              <div className="text-sm text-background/60 mt-3 italic">
                {t("home.howItWorks.flashcards.definition")}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div>
        <h4 className="font-display text-2xl md:text-3xl font-bold text-foreground mb-3">
          {t("home.howItWorks.flashcards.heading")}
        </h4>
        <p className="text-muted-foreground mb-6">
          {t("home.howItWorks.flashcards.body")}
        </p>
        <div className="grid grid-cols-3 gap-3">
          {stats.map((s) => (
            <div
              key={s.l}
              className="rounded-2xl bg-card border border-border p-4 text-center"
            >
              <div className="font-display text-3xl">{s.n}</div>
              <div className="text-xs text-muted-foreground mt-1">{s.l}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const puzzles: {
  id: ExerciseType;
  label: string;
  icon: typeof Volume2;
}[] = [
  { id: "multiple-choice", label: "Multiple choice", icon: CircleHelp },
  { id: "fill-in-the-blank", label: "Fill in the blank", icon: TextCursor },
  { id: "reorder-words", label: "Reorder words", icon: Shuffle },
  { id: "match-pairs", label: "Match pairs", icon: Combine },
  { id: "translate-sentence", label: "Translate sentence", icon: Languages },
  { id: "stroke-order", label: "Stroke order", icon: Pencil },
];

// The homepage demo runs the same server-graded play components, but there's no
// server here — so we strip the answer key into `data` (like the playable API
// would) and grade locally from the mock key to produce a `result`. Orders are
// fixed per exercise (fixedShuffle) so the server-rendered first view hydrates.
const MATCH_PAIRS_MISTAKE_LIMIT = 2;

export function toPlayableData(exercise: Exercise): Record<string, unknown> {
  switch (exercise.type) {
    case "multiple-choice":
      return { question: exercise.question, options: fixedShuffle(exercise.options, exercise.id) };
    case "fill-in-the-blank":
      return { sentence: exercise.sentence };
    case "reorder-words":
      return { words: fixedShuffle(exercise.correctSentence.split(" "), exercise.id) };
    case "match-pairs":
      return {
        words: fixedShuffle(exercise.pairs.map((p) => p.word), `${exercise.id}:words`),
        translations: fixedShuffle(exercise.pairs.map((p) => p.translation), `${exercise.id}:translations`),
        // The one answer key the real playable API ships (PuzzleExercise#playable_data):
        // MatchPairsPlay grades each pick client-side from it.
        pairs: exercise.pairs,
      };
    case "translate-sentence":
      return {
        sourceSentence: exercise.sourceSentence,
        sourceLanguage: exercise.sourceLanguage,
        targetLanguage: exercise.targetLanguage,
      };
    case "stroke-order":
      return {
        character: exercise.character,
        pinyin: exercise.pinyin,
        meaning: exercise.meaning,
        strokes: exercise.strokes,
        medians: exercise.medians,
      };
  }
}

export function gradeDemo(exercise: Exercise, answer: unknown): PlayProps["result"] {
  switch (exercise.type) {
    case "multiple-choice": {
      const correctOption = exercise.options[exercise.correctIndex];
      return {
        correct: answer === correctOption,
        solution: { options: exercise.options, correctIndex: exercise.correctIndex },
      };
    }
    case "fill-in-the-blank":
      return {
        correct: String(answer).trim().toLowerCase() === exercise.correctAnswer.toLowerCase(),
        solution: { correctAnswer: exercise.correctAnswer },
      };
    case "reorder-words":
      return {
        correct: (answer as string[]).join(" ") === exercise.correctSentence,
        solution: { correctSentence: exercise.correctSentence },
      };
    case "match-pairs": {
      // MatchPairsPlay submits once, when every pair is matched, as a
      // { pairs, mistakes } envelope; the mistake count decides pass or fail
      // (mirrors PuzzleGrader::MATCH_PAIRS_MISTAKE_LIMIT).
      const { mistakes } = answer as { pairs: unknown; mistakes: number };
      return { correct: mistakes < MATCH_PAIRS_MISTAKE_LIMIT, solution: { pairs: exercise.pairs } };
    }
    case "translate-sentence":
      return {
        correct:
          String(answer).trim().toLowerCase() ===
          exercise.correctTranslation.trim().toLowerCase(),
        solution: { correctTranslation: exercise.correctTranslation },
      };
    case "stroke-order":
      // Stroke grading is pending — neutral verdict.
      return { correct: true, solution: {} };
  }
}

export const PuzzlePlayground = () => {
  const { t } = useTranslation();
  const [active, setActive] = useState<ExerciseType>("multiple-choice");
  const [result, setResult] = useState<PlayProps["result"]>(null);
  const exercise = homepageDemoExercises.find((e) => e.type === active)!;
  const data = useMemo(() => toPlayableData(exercise), [exercise]);
  // Bumped by "Try again": clearing `result` alone leaves the play component's
  // own state behind (a finished match-pairs board stays complete and cannot
  // submit again), so each attempt mounts a fresh one.
  const [attempt, setAttempt] = useState(0);
  const answered = result !== null;

  return (
    <div className="grid md:grid-cols-5 gap-6">
      <div className="md:col-span-3 rounded-3xl bg-card border border-border p-6 md:p-8 shadow-soft">
        <ExerciseRenderer
          key={`${exercise.id}:${attempt}`}
          exerciseType={exerciseTypeToEnum(exercise.type)}
          data={data}
          result={result}
          onAnswer={(answer) => setResult(gradeDemo(exercise, answer))}
        />
        {answered && (
          <div className="mt-6 flex justify-center">
            <button
              type="button"
              onClick={() => {
                setResult(null);
                setAttempt((n) => n + 1);
              }}
              className="h-11 px-6 rounded-full bg-foreground text-background text-sm font-semibold flex items-center gap-2 focus-ring"
            >
              <RotateCcw className="w-4 h-4" />
              {t("home.howItWorks.puzzles.tryAgain")}
            </button>
          </div>
        )}
      </div>
      <div className="md:col-span-2 rounded-3xl bg-card border border-border p-6">
        <div className="text-sm font-display font-semibold text-foreground mb-4">
          {t("home.howItWorks.puzzles.pickType")}
        </div>
        <div className="space-y-2">
          {puzzles.map((p) => {
            const Icon = p.icon;
            const isActive = p.id === active;
            return (
              <button
                key={p.id}
                onClick={() => {
                  setActive(p.id);
                  setResult(null);
                }}
                className={`w-full flex items-center gap-3 p-4 rounded-2xl border-2 text-left transition focus-ring ${
                  isActive
                    ? "bg-foreground text-background border-foreground"
                    : "bg-card border-border hover:border-foreground"
                }`}
              >
                <span
                  className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                    isActive
                      ? "bg-primary text-primary-foreground"
                      : "bg-secondary"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                </span>
                <span className="font-semibold">{t(`home.howItWorks.puzzles.types.${p.id}`)}</span>
                {isActive && (
                  <Check className="w-4 h-4 ml-auto text-primary" />
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const LanguageExchange = () => {
  const { t, i18n } = useTranslation();
  const { stats } = usePlatformStats();
  // Real learner-community size, shown compactly (e.g. "+1.2K"). Gated on the
  // same credible-traction threshold as the hero strip (headlineVisible) so we
  // don't surface a weak pre-launch count or a fabricated "+12k".
  const communityCount = stats?.headlineVisible
    ? new Intl.NumberFormat(i18n.language, { notation: "compact", maximumFractionDigits: 1 }).format(stats.activeStudents)
    : null;
  return (
    <div className="grid md:grid-cols-2 gap-6">
      <div className="rounded-3xl bg-primary/20 p-8">
        <Globe2 className="w-8 h-8 mb-4" />
        <h4 className="font-display text-3xl mb-3">{t("home.howItWorks.exchange.heading")}</h4>
        <p className="text-foreground/70 mb-6">
          {t("home.howItWorks.exchange.body")}
        </p>
        <div className="flex -space-x-2">
          {[1, 2, 3, 4, 5].map((n) => (
            <div
              key={n}
              className="w-10 h-10 rounded-full bg-card border-2 border-primary/20 overflow-hidden"
            >
              <img
                src={`/images/homepage/headshots/headshot-${n}.webp`}
                alt={t("home.howItWorks.exchange.learnerAlt", { number: n })}
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </div>
          ))}
          {communityCount && (
            <div className="w-10 h-10 rounded-full bg-foreground text-background border-2 border-primary/20 flex items-center justify-center text-xs font-semibold">
              +{communityCount}
            </div>
          )}
        </div>
        <p className="mt-6 font-display text-3xl text-foreground">
          {t("home.howItWorks.exchange.communityHeading")}
        </p>
        <Button variant="outline" size="lg" asChild className="mt-3 w-fit">
          <Link to="/blog">{t("home.howItWorks.exchange.blogLink")}</Link>
        </Button>
      </div>
      <div className="rounded-3xl bg-card border border-border p-6 flex flex-col">
        <div className="flex items-center gap-3 pb-4 border-b border-border">
          <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center font-display">
            L
          </div>
          <div>
            <div className="font-semibold">{t("home.howItWorks.exchange.contactName")}</div>
            <div className="text-xs text-muted-foreground flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-success" />{" "}
              {t("home.howItWorks.exchange.contactStatus")}
            </div>
          </div>
        </div>
        <div className="flex-1 py-4 space-y-3">
          {/* Léa: greeting */}
          <div className="flex">
            <div className="max-w-[80%] p-3 rounded-2xl rounded-tl-sm bg-secondary text-sm">
              {t("home.howItWorks.exchange.chat.greeting")}
            </div>
          </div>

          {/* You: outgoing French with two beginner errors */}
          <div className="flex justify-end">
            <div className="max-w-[80%] p-3 rounded-2xl rounded-tr-sm bg-primary text-primary-foreground text-sm">
              <div>
                {t("home.howItWorks.exchange.chat.reply")}
              </div>
              <div className="mt-2 pt-2 border-t border-foreground/10 text-xs italic opacity-90">
                {t("home.howItWorks.exchange.chat.replyTranslation")}
              </div>
            </div>
          </div>

          {/* Léa: correction message — diff shown by default */}
          <div className="flex">
            <div className="max-w-[85%] p-3 rounded-2xl rounded-tl-sm bg-secondary text-sm">
              <div className="leading-relaxed">
                {t("home.howItWorks.exchange.chat.correctionPrefix")}{" "}
                <span className="line-through text-destructive">
                  {t("home.howItWorks.exchange.chat.correctionWrong")}
                </span>
                .
              </div>
              <div className="mt-2 pt-2 border-t border-foreground/10 leading-relaxed">
                {t("home.howItWorks.exchange.chat.correctionPrefix")}{" "}
                <span className="bg-success/20 text-success px-1 rounded">
                  {t("home.howItWorks.exchange.chat.correctionRight")}
                </span>
                .
              </div>
            </div>
          </div>
        </div>
        <div className="rounded-full bg-secondary px-4 h-11 flex items-center text-sm text-muted-foreground">
          {t("home.howItWorks.exchange.messagePlaceholder")}
        </div>
      </div>
    </div>
  );
};

const StepBar = ({
  number,
  label,
  first = false,
}: {
  number: string;
  label: string;
  first?: boolean;
}) => (
  <div
    className={`flex items-center gap-5 mb-8 ${first ? "" : "mt-20"}`}
  >
    <span className="font-display font-bold text-primary text-4xl md:text-6xl leading-none">
      {number}
    </span>
    <span className="h-px flex-1 bg-border" />
    <span className="text-lg md:text-xl font-display font-semibold text-foreground text-right">
      {label}
    </span>
  </div>
);

const HowItWorks = () => {
  const { t } = useTranslation();
  return (
    <section id="how-it-works" className="py-24 bg-background">
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-12">
            {t("home.howItWorks.title")}
          </h2>

          <StepBar number="01" label={t("home.howItWorks.steps.class")} first />
          <ClassroomVideoUI />

          <StepBar number="02" label={t("home.howItWorks.steps.courses")} />
          <MarketplacePreview />

          <StepBar number="03" label={t("home.howItWorks.steps.flashcards")} />
          <FlashSwipeCard />

          <StepBar number="04" label={t("home.howItWorks.steps.exercises")} />
          <div className="origin-top scale-[0.7] -mb-[140px] md:-mb-[180px]">
            <PuzzlePlayground />
          </div>

          <StepBar number="05" label={t("home.howItWorks.steps.chat")} />
          <LanguageExchange />
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
